import React from 'react'
import { RealEstateCategory, RealEstateFeatures, SegmentationType, TransactionType } from '../../../../../@types/types'
import { Flow, FlowName, FlowScreenElement } from '../../../../../@types/flow-types'
import { AGENT_GASTRONOMY_SELL_FLOW_PATH } from '../../../../../services/routes/routes'

import {
  SCREEN_ADDRESS,
  SCREEN_API_SEARCH_DATA,
  SCREEN_BASIC_INFO,
  SCREEN_CONNECTIONS,
  SCREEN_CONSTRUCTION,
  SCREEN_CONTACT_INFO,
  SCREEN_DESCRIPTION,
  SCREEN_MULTIMEDIA,
  SCREEN_OBJECT_NUMBERS,
  SCREEN_OTHER_FACILITIES,
  SCREENS,
} from '../../../../../services/domain/wizardScreens'
import ContactInfo from '../../../../../components/pages/insertionPage/screens/contactInfo/ContactInfo'
import Address from '../../../../../components/pages/insertionPage/screens/address/Address'
import Construction from '../../../../../components/pages/insertionPage/screens/construction/Construction'
import Multimedia from '../../../../../components/pages/insertionPage/screens/multimediaNew/Multimedia'

import { MultimediaUploaderType } from '../../../../../@types/multimedia-types'
import Description from '../../../../../components/pages/insertionPage/screens/description/Description'
import OtherFacilities from '../../../../../components/pages/insertionPage/screens/otherFacilities/OtherFacilities'
import {
  estateFeatures,
  freeFrom,
  interiorQuality,
  numberOfBeds,
  numberOfSeats,
  parkingSpace,
  totalFloorsField,
} from '../../../../../components/pages/insertionPage/screens/otherFacilities/wrapper/otherFacilitiesFieldsWrapper'
import { getDefaultClasses } from '../../../../../utils/style-utils'
import { priceToApiSell } from '../../../../../api/realEstate/converters/toApi/estate/price/priceToApi'
import BasicInfo from '../../../../../components/pages/insertionPage/screens/basicInfo/BasicInfo'
import {
  auxiliaryAreaField,
  courtageGroup,
  mainFloorSpaceField,
  priceField,
  purchasePriceOnRequestToggle,
  totalFloorSpaceField,
} from '../../../../../components/pages/insertionPage/screens/basicInfo/wrappers/basicInfoFieldsWrapper'
import { PriceFieldType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/priceField/PriceField'
import { buildingTypeField } from '../../../../../components/pages/insertionPage/screens/commonFields/buildingType/BuildingType'
import { BuildingTypeOption } from '../../../../../@types/dropdown-types'
import { MainFloorType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/mainFloorSpaceField/MainFloorSpaceField'
import Connections from '../../../../../components/pages/insertionPage/screens/connections/Connections'
import { ObjectNumbersScreen } from '../../../../../components/pages/insertionPage/screens/objectNumber/ObjectNumbersScreen'
import ApiSearchData from '../../../../../components/pages/insertionPage/screens/apiSearchData/ApiSearchData'
import { courtageInformationToApi } from '../../../../../api/realEstate/converters/toApi/estate/courtageInformation/courtageInformationToApi'
import { apiSearchDataToApi } from '../../../../../api/realEstate/converters/toApi/estate/apiSearchData/apiSearchDataToApi'
import { agentGastronomySellSchema } from './agentGastronomySellSchema'

const agentGastronomySellFlowScreens: FlowScreenElement[] = [
  {
    screen: SCREENS[SCREEN_ADDRESS],
    element: (props) => <Address {...props} />,
  },
  {
    screen: SCREENS[SCREEN_OBJECT_NUMBERS],
    element: (props) => <ObjectNumbersScreen {...props} />,
  },
  {
    screen: SCREENS[SCREEN_BASIC_INFO],
    element: (props) => (
      <BasicInfo
        {...props}
        screenTooltipKey='tooltip.screens.basicInfo.gastronomyRentSell'
        properties={[
          totalFloorSpaceField(),
          mainFloorSpaceField(MainFloorType.GASTRONOMY_AREA, 'right', undefined),
          auxiliaryAreaField('left'),
          buildingTypeField(
            'object',
            [
              BuildingTypeOption.BAR_LOUNGE,
              BuildingTypeOption.CAFE,
              BuildingTypeOption.GUESTS_HOUSE,
              BuildingTypeOption.TAVERN,
              BuildingTypeOption.HOTEL,
              BuildingTypeOption.HOTEL_RESIDENCE,
              BuildingTypeOption.HOTEL_GARNI,
              BuildingTypeOption.PENSION,
              BuildingTypeOption.RESTAURANT,
              BuildingTypeOption.BUNGALOW,
            ],
            getDefaultClasses('right')
          ),
          priceField(PriceFieldType.BUY_PRICE, getDefaultClasses('left')),
          freeFrom(getDefaultClasses('right')),
          purchasePriceOnRequestToggle(),
          courtageGroup(),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_DESCRIPTION],
    element: (props) => <Description {...props} hasFurnishingNote={true} hasLocationNote={true} hasOtherNote={true} />,
  },
  {
    screen: SCREENS[SCREEN_MULTIMEDIA],
    element: (props) => (
      <Multimedia
        {...props}
        doCloseMultimediaStatusMessage={props.doCloseMultimediaStatusMessage!}
        allowedTypesToDisplay={[
          MultimediaUploaderType.PICTURE,
          MultimediaUploaderType.FLOOR_PLAN,
          MultimediaUploaderType.DOCUMENT,
          MultimediaUploaderType.VIDEO,
        ]}
        allowMultimediaLinks={true}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_OTHER_FACILITIES],
    element: (props) => (
      <OtherFacilities
        screenTooltipKey='tooltip.screens.otherFacilities.allSell'
        {...props}
        properties={[
          estateFeatures([
            RealEstateFeatures.BASEMENT,
            RealEstateFeatures.LISTED,
            RealEstateFeatures.PERSON_ELEVATOR,
            RealEstateFeatures.TERRACE,
          ]),
          interiorQuality(),
          numberOfSeats(getDefaultClasses('left')),
          numberOfBeds(getDefaultClasses('right')),
          totalFloorsField(getDefaultClasses('left')),
          parkingSpace({ showParkingSpaceType: false, parkingSpaceRentTypeLabel: 'RentPerItem' }),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_CONNECTIONS],
    element: (props) => <Connections {...props} />,
  },
  {
    screen: SCREENS[SCREEN_CONSTRUCTION],
    element: (props) => <Construction {...props} hasLastModernizationField={true} />,
  },

  {
    screen: SCREENS[SCREEN_API_SEARCH_DATA],
    element: (props) => <ApiSearchData {...props} />,
  },
  {
    screen: SCREENS[SCREEN_CONTACT_INFO],
    element: (props) => <ContactInfo {...props} hasContactManager={true} />,
  },
]

export const agentGastronomySellFlow: Flow = {
  flowInformation: {
    owner: 'wizard.navigation.header.agent',
    estateType: 'wizard.navigation.header.gastronomy',
    offerType: 'wizard.navigation.header.sells',
    path: AGENT_GASTRONOMY_SELL_FLOW_PATH,
    name: FlowName.AGENT_GASTRONOMY_SELL,
    validationSchema: agentGastronomySellSchema,
    priceToApiMapper: priceToApiSell,
    courtageToApiMapper: courtageInformationToApi,
    apiSearchDataToApiMapper: apiSearchDataToApi,
    metaData: {
      category: RealEstateCategory.GASTRONOMY,
      segmentation: SegmentationType.AGENT,
      transaction: TransactionType.SELL,
    },
  },
  defaultStateValues: { hideStreetAndStreetNumber: false },
  flowScreens: agentGastronomySellFlowScreens,
}
